import { useEffect, type ReactNode } from 'react';

/**
 * Bottom sheet — единственная форма модального окна в приложении (ТЗ §14.3:
 * «Bottom sheet вместо полноэкранных модалок»).
 *
 * Почему не полноэкранная модалка: внутри Telegram у неё нет своего крестика в системной
 * шапке, и закрыть её можно только кнопкой внутри. Промахнись — и человек закрывает жестом
 * само Mini App, теряя незавершённую запись. Шторка оставляет видимым экран под собой,
 * и тап по затемнению — очевидный выход.
 */
export function BottomSheet(props: {
  open: boolean;
  title?: string | undefined;
  onClose: () => void;
  children: ReactNode;
}): JSX.Element | null {
  const { open, onClose } = props;

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="animate-fade-in absolute inset-0 z-30 flex items-end bg-black/45"
      onClick={(event) => {
        // Закрытие только по фону: клик внутри шторки не должен её ронять,
        // иначе промах по кнопке отменяет уже введённые данные.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="animate-sheet-up no-scrollbar max-h-[88%] w-full overflow-y-auto rounded-t-sheet bg-surface px-4 pb-[calc(env(safe-area-inset-bottom,0px)+18px)] pt-2.5 shadow-[0_-10px_34px_rgba(0,0,0,.32)]">
        <div className="mx-auto mb-3 h-1 w-9 rounded-full bg-hair" />
        {props.title ? (
          <div className="mb-3.5 text-[17px] font-extrabold tracking-tight">{props.title}</div>
        ) : null}
        {props.children}
      </div>
    </div>
  );
}
